import { FormattedMessage, useIntl } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { useTableContext } from '@/contexts/table-context';

import { useHiresToDate } from './context';
import { hiresToDateTimeline } from './timeline';

// The end-date label hangs off its marker away from the nearer edge, and stops
// short of the start and Today labels (each about an eighth of the bar).
function endLabelPosition(percent: number) {
  if (percent <= 30) {
    return { left: `${Math.max(percent, 16)}%`, transform: 'none' };
  }
  if (percent >= 70) {
    return {
      left: `${Math.min(percent, 84)}%`,
      transform: 'translateX(-100%)',
    };
  }
  return { left: `${percent}%`, transform: 'translateX(-50%)' };
}

/**
 * The Hires to date explainer's timeline for the selected past range: the range
 * in brand, the time since it ended dashed, and an example applicant sourced
 * inside it and hired after it.
 */
export function HiresToDateTimeline({
  hiresAfterRangeEnd,
}: {
  /** Applicants in the range hired after its end date, from the metrics API. */
  hiresAfterRangeEnd: number;
}) {
  const intl = useIntl();
  const { isEligible, today } = useHiresToDate();
  const { dateRange } = useTableContext();

  if (!isEligible || !dateRange.startDate || !dateRange.endDate) return null;

  const timeline = hiresToDateTimeline(
    dateRange.startDate,
    dateRange.endDate,
    today,
  );
  const day = (date: Date) =>
    intl.formatDate(date, { month: 'short', day: '2-digit' });

  return (
    <div className="bg-fx-neutral-50 flex flex-col gap-1.5 rounded-md px-3 pt-2.5 pb-3">
      <div className="relative h-11" aria-hidden="true">
        <div
          className="bg-fx-brand-600 absolute top-[22px] left-0 h-1.5 rounded-l-full"
          style={{ width: `${timeline.endPercent}%` }}
        />
        <div
          className="absolute top-[22px] right-0 h-1.5 rounded-r-full bg-[repeating-linear-gradient(90deg,var(--color-fx-brand-100)_0_6px,transparent_6px_9px)]"
          style={{ left: `${timeline.endPercent}%` }}
        />
        <Typography
          variant="small1"
          className="text-fx-neutral-600 absolute top-0 left-0"
        >
          {day(timeline.start)}
        </Typography>
        <Typography
          variant="small1"
          className="text-fx-neutral-600 absolute top-0 whitespace-nowrap"
          style={endLabelPosition(timeline.endPercent)}
        >
          {day(timeline.end)}
        </Typography>
        <Typography variant="small2" className="absolute top-0 right-0">
          <FormattedMessage
            defaultMessage="Today"
            description="Right end of the Hires to date timeline on All Campaigns, marking today"
          />
        </Typography>
        <div
          className="bg-fx-neutral-900 absolute top-[19px] size-3 -translate-x-1/2 rounded-full border-2 border-white"
          style={{ left: `${timeline.sourcedPercent}%` }}
        />
        <div
          className="bg-fx-blue absolute top-[19px] size-3 -translate-x-1/2 rounded-full border-2 border-white"
          style={{ left: `${timeline.hiredPercent}%` }}
        />
      </div>
      {hiresAfterRangeEnd > 0 && (
        <Typography variant="small1" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="{count, plural, one {# applicant sourced by {endDate} was hired after it} other {# applicants sourced by {endDate} were hired after it}}: left out of Hires, counted in Hires to date."
            description="Under the Hires to date timeline on All Campaigns: how many of the range's applicants were hired after its end date, which only Hires to date counts"
            values={{ count: hiresAfterRangeEnd, endDate: day(timeline.end) }}
          />
        </Typography>
      )}
    </div>
  );
}
